import React, { useState } from 'react';
import {
  Box, Typography, Stack, Accordion, AccordionSummary, AccordionDetails,
  Chip, Divider, Tab, Tabs, Paper, Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import PaymentIcon from '@mui/icons-material/Payment';
import UndoIcon from '@mui/icons-material/Undo';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const COLOR = '#ff6eb4';

// ── Shared content ────────────────────────────────────────────────────────────

const termsOfUse = {
  icon: <GavelIcon />,
  title: 'Terms of Use',
  items: [
    { type: 'text', content: 'All rights are reserved by panko. The artist (panko) holds the copyright of the commissioned model.' },
    { type: 'text', content: 'The artist can promote and post the artwork as a portfolio.' },
    { type: 'header', content: 'The commissioner is allowed to:' },
    { type: 'bullet', content: 'Use the copyrighted artwork for personal purpose.' },
    { type: 'bullet', content: 'Use the model to promote themselves with proper credit given to the artist where possible.' },
    { type: 'bullet', content: 'Use the model to stream, make social media content, or play social games.' },
    { type: 'bullet', content: 'Pay someone else to make edits to the work.' },
    { type: 'text', content: 'If you wish to use the model and all its copyrights (reselling and commercial use), additional fees (at least 200% of the original price) or royalties, as well as proper credit, will be required.' },
  ],
};

const ndaSection = {
  icon: <LockIcon />,
  title: 'Non-Disclosure Agreement (NDA)',
  items: [
    { type: 'bullet', content: 'If the work needs to be kept confidential (e.g. for debuts), you can request an NDA. I charge an additional 10% of the total price for NDA work.' },
    { type: 'bullet', content: 'The artist is free to post the work on social media once the model has been made public.' },
    { type: 'bullet', content: 'If you want a permanent NDA, please specify this upfront.' },
  ],
};

// ── Per-tier content ──────────────────────────────────────────────────────────

const tiers = {
  custom: {
    label: 'Custom',
    priceTag: 'On request',
    payment: {
      icon: <PaymentIcon />,
      title: 'Payment Policy',
      items: [
        { type: 'bullet', content: 'Pricing is quoted individually based on your project scope — reach out to discuss before commissioning.' },
        { type: 'bullet', content: 'Payments are made via PayPal.' },
        { type: 'bullet', content: 'Payment terms (upfront or split) are agreed per project.' },
        { type: 'bullet', content: 'Payment plans are available on request.' },
      ],
    },
    refund: {
      icon: <UndoIcon />,
      title: 'Refund Policy',
      items: [
        { type: 'bullet', content: 'Refund terms are agreed on a per-project basis before work begins.' },
        { type: 'bullet', content: 'Once work has started, partial refunds may apply depending on what has been completed.' },
        { type: 'bullet', content: 'Please make sure you are happy with the quoted scope and terms before paying.' },
      ],
    },
    faqs: [
      {
        q: 'What counts as a custom project?',
        a: 'Anything that doesn\'t fit neatly into the Low Poly or High Poly tiers — chibi models, props, accessories, scene assets, game-ready characters, stylised models for specific platforms, or any project with unusual requirements.',
      },
      {
        q: 'How do I request a custom commission?',
        a: 'Reach out via VGen or the contact form with a description of your project, any reference images, and your target platform. I\'ll get back to you with a quote.',
      },
      {
        q: 'How long does a custom project take?',
        a: 'Timeline depends entirely on the scope. Simple props or chibi models may take a few days. More involved projects are scoped individually during the quote process.',
      },
      {
        q: 'How many revisions are included?',
        a: 'Revision rounds are agreed as part of the quote. Typically 2 revisions for smaller projects, more for larger ones.',
      },
      {
        q: 'What files will I receive?',
        a: 'Deliverables are agreed per project. Common formats include VRM, FBX, .blend, and platform-specific exports. This will be outlined clearly in your quote.',
      },
      {
        q: 'Do you do furry / anthro designs?',
        a: 'Yes — custom commissions are a great fit for non-standard character types like furries, anthros, and other non-human designs.',
      },
      {
        q: 'Do you do mecha or magical girl designs?',
        a: 'Yes. Complex or stylised designs are welcome as custom requests. Difficulty and detail level will be reflected in the quote.',
      },
    ],
  },
  lowpoly: {
    label: 'Low Poly',
    priceTag: 'From $200',
    payment: {
      icon: <PaymentIcon />,
      title: 'Payment Policy',
      items: [
        { type: 'bullet', content: 'Low poly commissions start at $200. Full payment is required upfront before work begins.' },
        { type: 'bullet', content: 'Payments are made via PayPal.' },
        { type: 'bullet', content: 'Payment plans are available on request — please ask before commissioning.' },
      ],
    },
    refund: {
      icon: <UndoIcon />,
      title: 'Refund Policy',
      items: [
        { type: 'bullet', content: 'No refunds are offered on low poly commissions.' },
        { type: 'bullet', content: 'Please make sure you are happy with the terms before paying.' },
      ],
    },
    faqs: [
      {
        q: 'How long does a low poly commission take?',
        a: 'Low poly commissions are simpler and typically completed within a few days.',
      },
      {
        q: 'How many revisions are included?',
        a: '2 revisions are included after delivery. Additional revisions are $100 each.',
      },
      {
        q: 'What files will I receive?',
        a: 'You will receive a VRM file and a Warudo setup.',
      },
      {
        q: 'Are payment plans available?',
        a: 'Yes, on request. Please reach out before commissioning to arrange this.',
      },
      {
        q: 'Do you do furry / anthro avatars?',
        a: 'Yes! Furry and anthro designs are welcome. Depending on the complexity of the design (fur details, claws, tails, etc.) there may be a price adjustment.',
      },
      {
        q: 'Do you do mecha designs?',
        a: 'Yes, mecha and mechanical elements are supported. Hard-surface modelling is handled differently from organic characters and will be reflected in the quote.',
      },
      {
        q: 'Do you do magical girl designs?',
        a: 'Absolutely — magical girl aesthetics are a great fit. Complex outfits, layered fabric, and accessories may affect the final price.',
      },
    ],
  },
  highpoly: {
    label: 'High Poly',
    priceTag: '$4,000+',
    payment: {
      icon: <PaymentIcon />,
      title: 'Payment Policy',
      items: [
        { type: 'bullet', content: 'Payment is collected on the day work begins. I start after the first payment is received.' },
        { type: 'bullet', content: 'Payments are made via PayPal.' },
        { type: 'bullet', content: '50% upfront payment is charged at the start.' },
        { type: 'bullet', content: 'The remaining 50% is charged right before handing over the model.' },
      ],
    },
    refund: {
      icon: <UndoIcon />,
      title: 'Refund Policy',
      items: [
        { type: 'bullet', content: 'After modelling has started, a full refund will not be provided.' },
        { type: 'bullet', content: 'If cancellation occurs from your side, I will keep the initial 50% deposit and retain all rights to the model and WIPs.' },
        { type: 'bullet', content: 'If cancellation occurs from my side, I will refund the initial 50% deposit and dispose of the model and WIPs.' },
        { type: 'bullet', content: 'Once the model is handed over, refund requests are not accepted.' },
      ],
    },
    faqs: [
      {
        q: 'How long does each commission take?',
        a: 'It usually takes 2 months or more. It takes some time to cook!',
      },
      {
        q: 'Do you do NSFW avatars?',
        a: 'Depending on the design, we can discuss it. NSFW basebody is 3× the normal basebody price ($600).',
      },
      {
        q: 'Do I need to add the basebody option?',
        a: "It depends on your future outfit plans. In 3D modelling, unseen parts are generally not modelled to optimise the model. If you have a basebody, you can use it when making new outfits later.",
      },
      {
        q: 'How many revisions are included?',
        a: 'You can review progress (WIPs) and request changes along the way. After the model is handed over, I offer up to 4 revisions. Additional revisions beyond the initial 4 are $100 each.',
      },
      {
        q: 'What files will I receive?',
        a: 'You will receive a .Warudo file, .VRM, .FBX, Blender source file with PNG textures, and a VRChat avatar 3.0 UnityPackage.',
      },
      {
        q: 'What design elements increase the base price?',
        a: '',
        list: [
          'Long hair — requires detailed rigging for natural movement and collision.',
          'Long skirts or dresses — need rigging to avoid overlaps and manage interactions.',
          'Accessories (e.g. necklaces, earrings) — require extra setup to prevent tangling and ensure natural sway.',
          'Jackets or cloaks — complex rigging needed to handle draping and body collision.',
          'Layered or long sleeves — risk of overlap or tearing, requiring careful rigging.',
          'Complex shading style — requires detailed and extensive texture work.',
          'Sharp or intricate details — add to modelling and performance optimisation challenges.',
          'Multiple attachment points — need precise adjustments to avoid accessory interference.',
          'Asymmetrical designs — more complex to balance in modelling and animation.',
          'Transparent or reflective materials — require advanced texturing and shader setup.',
        ],
      },
      {
        q: 'Do you do corporate commissions?',
        a: 'Yes, but at a different rate. Please contact me directly to discuss.',
      },
      {
        q: 'Can I get a referral for high-level technical support?',
        a: 'Yes — for advanced technical needs I can refer you to a trusted partner at Mofu Workshop.',
      },
      {
        q: 'Do high poly models include jiggle physics?',
        a: 'Yes — jiggle physics are included as standard on high poly commissions. This covers breast physics, hair jiggle, and accessory sway. The implementation depends on your target platform.',
      },
      {
        q: 'What is the difference between Magica Cloth and Springbones?',
        a: 'VRM Springbones are the standard physics system for VRM models and work across most VRM-compatible apps including VTube Studio. Magica Cloth is a more advanced simulation used in Warudo that handles complex cloth interactions like skirts, capes, and layered fabric with much more realism and fewer clipping issues. High poly commissions include both — Springbones for VRM/VRChat compatibility and Magica Cloth setup for Warudo.',
      },
      {
        q: 'Do you do furry / anthro avatars?',
        a: 'Yes! Furry and anthro designs are fully supported. Elements like detailed fur, digitigrade legs, tails, claws, and non-human facial structures all add complexity and will be reflected in the quote.',
      },
      {
        q: 'Do you do mecha designs?',
        a: 'Yes — mecha and hard-surface characters are a different challenge from organic modelling but very much supported. Mechanical joints, armour panelling, and transformation gimmicks will each be scoped individually.',
      },
      {
        q: 'Do you do magical girl designs?',
        a: 'Absolutely. Magical girl aesthetics — frills, ribbons, layered skirts, wands, wings — are a great fit. Each element affects rigging complexity and will be factored into the final price.',
      },
    ],
  },
};

type TierKey = keyof typeof tiers;

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionBlock({ icon, title, items }: { icon: React.ReactNode; title: string; items: { type: string; content: string }[] }) {
  return (
    <Box sx={{
      background: 'rgba(22,15,32,0.8)',
      border: `1px solid ${COLOR}22`,
      borderRadius: 3,
      p: { xs: 3, md: 4 },
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ color: COLOR, display: 'flex' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{title}</Typography>
      </Stack>
      <Stack spacing={1.5}>
        {items.map((item, i) => {
          if (item.type === 'header') return (
            <Typography key={i} sx={{ fontWeight: 600, color: 'text.primary', mt: 1 }}>{item.content}</Typography>
          );
          if (item.type === 'bullet') return (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ mt: '6px', width: 6, height: 6, borderRadius: '50%', background: COLOR, flexShrink: 0 }} />
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.92rem' }}>{item.content}</Typography>
            </Stack>
          );
          return (
            <Typography key={i} sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.92rem' }}>{item.content}</Typography>
          );
        })}
      </Stack>
    </Box>
  );
}

function FaqList({ faqs }: { faqs: typeof tiers.highpoly.faqs }) {
  const [expanded, setExpanded] = useState<number | false>(false);
  return (
    <Stack spacing={1.5}>
      {faqs.map((faq, i) => (
        <Accordion
          key={i}
          expanded={expanded === i}
          onChange={() => setExpanded(expanded === i ? false : i)}
          disableGutters
          sx={{
            background: 'rgba(22,15,32,0.8)',
            border: `1px solid ${expanded === i ? COLOR + '55' : COLOR + '18'}`,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            transition: 'border-color 0.2s',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: COLOR }} />} sx={{ px: 3, py: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{faq.q}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
            <Divider sx={{ borderColor: `${COLOR}22`, mb: 2 }} />
            {faq.a && (
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.92rem', mb: faq.list ? 2 : 0 }}>
                {faq.a}
              </Typography>
            )}
            {faq.list && (
              <Stack spacing={1}>
                {faq.list.map((item, j) => (
                  <Stack key={j} direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography sx={{ color: COLOR, fontWeight: 700, fontSize: '0.85rem', flexShrink: 0, mt: '2px' }}>
                      {j + 1}.
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.92rem' }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

function TrackingSection() {
  return (
    <Box sx={{
      background: 'rgba(22,15,32,0.8)',
      border: `1px solid ${COLOR}22`,
      borderRadius: 3,
      p: { xs: 3, md: 4 },
    }}>
      <Typography variant="overline" sx={{ color: COLOR, letterSpacing: 4, fontSize: '0.7rem' }}>
        FACIAL TRACKING
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
        VRM Tracking vs PerfectSync ARKit
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.8, mb: 4 }}>
        VRM blendshape tracking is included in every model. PerfectSync ARKit is a more advanced system that is evaluated per commission.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{
            p: 3, height: '100%',
            background: `${COLOR}08`,
            border: `1px solid ${COLOR}22`,
            borderRadius: 2,
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                background: `${COLOR}22`, border: `1px solid ${COLOR}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: COLOR }}>VRM</Typography>
              </Box>
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>VRM Blendshape Tracking</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: COLOR }}>Included by default</Typography>
              </Stack>
            </Stack>
            <Stack spacing={1.2}>
              {[
                'Works with any webcam or phone using VTube Studio or similar software.',
                'Drives a set of predefined VRM expressions — blink, happy, angry, sad, surprised — mapped to your face movements in real time.',
                'Compatible with every VRM-based app: VTube Studio, Warudo, VRChat, and more.',
                'The standard tracking system used by the vast majority of VTubers. No extra hardware needed.',
              ].map((line, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', background: COLOR, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.88rem' }}>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{
            p: 3, height: '100%',
            background: 'rgba(180,79,255,0.06)',
            border: '1px solid rgba(180,79,255,0.25)',
            borderRadius: 2,
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(180,79,255,0.15)', border: '1px solid rgba(180,79,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#b44fff', lineHeight: 1, textAlign: 'center' }}>ARKit</Typography>
              </Box>
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>PerfectSync ARKit</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#b44fff' }}>Evaluated per model</Typography>
              </Stack>
            </Stack>
            <Stack spacing={1.2}>
              {[
                'Requires an iPhone with Face ID (TrueDepth camera).',
                'Tracks 52 individual ARKit blendshapes — where VRM drives broad preset expressions, ARKit captures the raw movement of your actual face directly: eyebrows, cheek puffs, jaw slide, lip curl, tongue, and squints, all independently.',
                'The result is significantly more nuanced animation that mirrors your real face rather than approximating it with presets.',
                'Not included by default — the 52 blendshapes must be manually authored for each model. Whether this is feasible depends on the character\'s facial structure and is scoped during the commission.',
              ].map((line, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', background: '#b44fff', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.88rem' }}>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function PhysicsSection() {
  return (
    <Box sx={{
      background: 'rgba(22,15,32,0.8)',
      border: `1px solid ${COLOR}22`,
      borderRadius: 3,
      p: { xs: 3, md: 4 },
    }}>
      <Typography variant="overline" sx={{ color: COLOR, letterSpacing: 4, fontSize: '0.7rem' }}>
        PHYSICS
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
        MagicaCloth 2 vs VRM Springbones
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.8, mb: 4 }}>
        Your model uses two different physics systems depending on the platform. Here's what each one does.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{
            p: 3, height: '100%',
            background: `${COLOR}08`,
            border: `1px solid ${COLOR}22`,
            borderRadius: 2,
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                background: `${COLOR}22`, border: `1px solid ${COLOR}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: COLOR, lineHeight: 1, textAlign: 'center' }}>SPR</Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>VRM Springbones</Typography>
            </Stack>
            <Stack spacing={1.2}>
              {[
                'The standard physics system built into the VRM spec.',
                'Works across all VRM-compatible apps — VTube Studio, Warudo, VRChat, and more.',
                'Handles hair, ears, tails, and light accessory sway.',
                'Simple chain-based simulation — lightweight and broadly compatible.',
                'Best for: maximum compatibility across platforms.',
              ].map((line, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', background: COLOR, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.88rem' }}>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{
            p: 3, height: '100%',
            background: 'rgba(180,79,255,0.06)',
            border: '1px solid rgba(180,79,255,0.25)',
            borderRadius: 2,
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(180,79,255,0.15)', border: '1px solid rgba(180,79,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#b44fff', lineHeight: 1, textAlign: 'center' }}>MC2</Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>MagicaCloth 2</Typography>
            </Stack>
            <Stack spacing={1.2}>
              {[
                'Advanced cloth simulation used exclusively in Warudo.',
                'Handles complex fabric — skirts, capes, scarves, ribbons — with realistic draping and collision.',
                'Significantly reduces clipping on layered or flowing outfits.',
                'Mesh-based simulation rather than chain-based, giving far more natural movement.',
                'Best for: Warudo users who want high-quality cloth physics on complex outfits.',
              ].map((line, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', background: '#b44fff', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.88rem' }}>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', mt: 3, fontStyle: 'italic' }}>
        All low poly and high poly models include both — Springbones for cross-platform compatibility and MagicaCloth 2 setup for Warudo. You get the best of both without any trade-off.
      </Typography>
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function TosPage() {
  const [tab, setTab] = useState<TierKey>('lowpoly');
  const tier = tiers[tab];

  return (
    <Box sx={{ background: '#0d0a12', minHeight: '100vh', pt: 12, pb: 16 }}>
      {/* Hero */}
      <Box sx={{ px: { xs: 3, md: 8 }, pb: 6, maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
        <Chip
          label="COMMISSION INFO"
          size="small"
          sx={{
            background: `${COLOR}18`, border: `1px solid ${COLOR}44`,
            color: COLOR, fontWeight: 600, letterSpacing: 2, fontSize: '0.7rem', mb: 3,
          }}
        />
        <Typography variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 700, mb: 2 }}>
          Terms of Service
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.8, maxWidth: 600, mx: 'auto' }}>
          Please read before applying for a commission. By commissioning me you agree to all terms below.
        </Typography>
      </Box>

      {/* Tier tabs */}
      <Box sx={{ px: { xs: 3, md: 8 }, maxWidth: 900, mx: 'auto', mb: 5 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: `1px solid ${COLOR}22`,
            '& .MuiTab-root': {
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
              '&.Mui-selected': { color: COLOR },
            },
            '& .MuiTabs-indicator': { background: COLOR },
          }}
        >
          {(Object.entries(tiers) as [TierKey, typeof tiers[TierKey]][]).map(([key, t]) => (
            <Tab
              key={key}
              value={key}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t.label}</span>
                  <Chip
                    label={t.priceTag}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.65rem', fontWeight: 600,
                      background: tab === key ? `${COLOR}22` : 'rgba(255,255,255,0.05)',
                      color: tab === key ? COLOR : 'text.secondary',
                      border: `1px solid ${tab === key ? COLOR + '55' : 'transparent'}`,
                    }}
                  />
                </Stack>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 3, md: 8 }, maxWidth: 900, mx: 'auto' }}>
        <Stack spacing={3}>
          <SectionBlock {...termsOfUse} />
          <SectionBlock {...tier.payment} />
          <SectionBlock {...tier.refund} />
          <SectionBlock {...ndaSection} />
        </Stack>

        {/* FAQ */}
        <Box sx={{ mt: 8 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 4 }}>
            <HelpOutlineIcon sx={{ color: COLOR }} />
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
              FAQ
            </Typography>
          </Stack>
          <FaqList faqs={tier.faqs} />
        </Box>
      </Box>
    </Box>
  );
}
